import { classMap, customElement, html, repeat } from '@adobe/edex/ui/edex-lit';
import { BaseAuthElement } from '@adobe/edex/ui/components/base.element';
import { EdexTagName, SURVEY_CONTENT_ACQUIRED_COOKIE_KEY } from '@adobe/edex/ui/shared/constants';
import { LessonStepsSchema, MarshalledResource } from '@adobe/edex/ui/types';
import '@adobe/edex/ui/components/resource/resource-sidebar/resource-sidebar.scss';
import '@adobe/edex/ui/components/resource/resource-sidebar/tools';
import { RichTextInlineLinkClicked } from '@adobe/edex/ui/types/payload';
import {
  ccxTemplatePatternMatching,
  isCCXAcquisitionLink,
  setSurveyCookieForNoCAParams,
  trackTargetEvent,
} from '@adobe/edex/ui/utils';
import { AnalyticsEvent } from '@adobe/edex/ui/constants';
import { FullStoryUtils } from '@adobe/edex/ui/shared/config/fullstory';
import { Analytics } from '@adobe/edex/ui/shared/config/analytics';
import '@adobe/edex/ui/components/shared/edex-html/edex-html';
import '@adobe/edex/ui/components/resource/resource-meta/resource-meta';
import { $t } from '@adobe/edex/ui/shared/config/i18n';
import { I18nKey } from '@adobe/edex/ui/shared/constants/i18n';

@customElement(EdexTagName.resourceSidebar)
export class ResourceSideBar extends BaseAuthElement {
  static get properties() {
    return { ...super.properties, resource: { attribute: false }, activeTab: { attribute: false } };
  }

  resource: MarshalledResource;
  activeTab = 'learningObjectives';

  get useLightDom() {
    return true;
  }

  handleTabClick = (event) => {
    const id = event.target.id;
    switch (id) {
      case 'learningObjectives':
        this.activeTab = 'learningObjectives';
        break;
      case 'tools':
        this.activeTab = 'tools';
        break;
      case 'lessonSteps':
        this.activeTab = 'lessonSteps';
        break;
      default:
        this.activeTab = 'learningObjectives';
    }
  };

  handleInlineLinkClick({ detail: { link } }: CustomEvent<RichTextInlineLinkClicked>) {
    const url = link.getAttribute('href');
    const ccxtemplateUrl = ccxTemplatePatternMatching(url);
    const isCCXAcquisitionUrl = isCCXAcquisitionLink(url);
    const event = ccxtemplateUrl
      ? AnalyticsEvent.resourceClickCCXTemplateLinkInline
      : isCCXAcquisitionUrl
      ? AnalyticsEvent.resourceClickCCXAcquisition
      : AnalyticsEvent.resourceClickInlineLink;
    this.track({
      event,
      payload: {
        id: this.resource.id,
        url,
      },
    });

    FullStoryUtils.getInstance().event(event, {
      resourceID: this.resource.id,
      vanityURL: this.resource.vanityURL,
      text: link.innerHTML?.replace(/<[^>]*>?/gm, ''),
      href: url,
    });

    if (ccxtemplateUrl || isCCXAcquisitionUrl) {
      Analytics.getInstance().emitEvent(
        event,
        `Open link in CC Express from resource description to ${link.getAttribute('href')}`
      );
      trackTargetEvent('edex-resource-ccx-template-inline-click');
    } else {
      trackTargetEvent('edex-resource-inline-click');
    }
    if (!this.isGuest) {
      setSurveyCookieForNoCAParams(SURVEY_CONTENT_ACQUIRED_COOKIE_KEY);
    }
  }

  renderLearningObjectivesContent = () => {
    const {
      activeTab,
      resource: { description },
    } = this;
    if (activeTab === 'learningObjectives') {
      return html`
        <edex-html .source=${description} @linkClicked="${this.handleInlineLinkClick}"></edex-html>
      `;
    } else {
      return null;
    }
  };

  renderToolsContent = () => {
    const {
      activeTab,
      resource: { id, products },
    } = this;
    if (activeTab === 'tools') {
      return html`
        <edex-tools-new .products=${products} .resourceID=${id} />
      `;
    } else {
      return null;
    }
  };

  renderShortDescription = () => {
    const {
      resource: { shortDescription },
    } = this;

    return html`
      <div class="short-description">
        <p>${shortDescription}</p>
      </div>
    `;
  };

  renderLessonStepsContent = () => {
    const {
      activeTab,
      resource: { lessonSteps },
    } = this;
    if (activeTab === 'lessonSteps') {
      return html`
        <div class="lesson-steps-wrapper">${repeat(lessonSteps, this.renderLessonStep)}</div>
      `;
    } else {
      return null;
    }
  };

  renderLessonStep = (lessonStep: LessonStepsSchema, index: number) => {
    const stepName: string = $t(lessonStep.i18nLabel);
    return html`
      <div class="lesson-step">
        <img src=${lessonStep.imageURL} class="lesson-step-image" />
        <p class="lesson-step-label" title=${stepName}>
          ${index + 1}. ${stepName.length <= 20 ? stepName : stepName.slice(0, 20) + '...'}
        </p>
      </div>
    `;
  };

  renderMeta = () => {
    return html`
      <edex-resource-meta .resource=${this.resource} />
    `;
  };

  render() {
    const { resource, activeTab } = this;
    if (!resource) {
      return null;
    }

    return html`
      <div class="resource-side-bar">
        ${this.renderMeta()} ${this.renderShortDescription()}
        <div class="tabs">
          <span
            id="learningObjectives"
            @click=${this.handleTabClick}
            class="${classMap({
              active: activeTab === 'learningObjectives',
              tab: true,
            })}"
            tabindex="0"
          >
            ${$t(I18nKey.resourceSidebarTabLearningObjectives)}
          </span>
          ${resource.lessonSteps.length !== 0
            ? html`
                <span
                  id="lessonSteps"
                  @click=${this.handleTabClick}
                  class="${classMap({
                    active: activeTab === 'lessonSteps',
                    tab: true,
                  })}"
                >
                  ${$t(I18nKey.studentStepsTabName)}
                </span>
              `
            : null}

          <span
            id="tools"
            @click=${this.handleTabClick}
            class="${classMap({
              active: activeTab === 'tools',
              tab: true,
            })}"
            tabindex="0"
          >
            ${$t(I18nKey.resourceSidebarTabTools)}
          </span>
        </div>
        <div class="tab-content">
          ${this.renderLearningObjectivesContent()}${this.renderToolsContent()}${this.renderLessonStepsContent()}
        </div>
      </div>
    `;
  }
}
